import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[0.625rem] text-sm font-semibold ring-offset-background transition-all duration-250 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[image:var(--grad-steel)] text-primary-foreground shadow-[var(--shadow-button)] hover:bg-[image:var(--grad-steel-hover)] hover:-translate-y-px hover:shadow-[var(--shadow-button-hover)] active:translate-y-0",
        primary: "bg-[image:var(--grad-steel)] text-primary-foreground shadow-[var(--shadow-button)] hover:bg-[image:var(--grad-steel-hover)] hover:-translate-y-px hover:shadow-[var(--shadow-button-hover)] active:translate-y-0",
        secondary: "bg-steel-light text-foreground hover:bg-steel-mid hover:text-white hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)]",
        ghost: "border-2 border-primary text-primary bg-transparent hover:bg-primary-light",
        "ghost-light": "border-2 border-white/30 text-white bg-transparent hover:bg-white/10 hover:border-champagne/50",
        hero: "bg-champagne text-charcoal font-bold hover:shadow-[0_0_16px_rgba(216,184,120,0.35)] hover:-translate-y-0.5 active:translate-y-0",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-line-2 bg-background text-ink hover:bg-tint",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-12 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
